<!-- =========================================================================================
  File Name: CompanyEditGeneralDetails.vue
  Description: Entity Edit account content
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="page-profile-form" :class="readonly ? 'readonly-form' : ''">
    <card-page card-title="Entity Profile">
      <vue-element-loading :active="busy"></vue-element-loading>
      <vue-form-generator
        class="mt-4"
        ref="form"
        :model="model"
        :options="formOptions"
        :schema="schema"
      />
    </card-page>
  </div>
</template>

<script>
import FormModelMixin from '@/components/mixins/FormModelMixin';
import CardPage from '../../../components/Views/CardPage';
import { selectOptions}  from '@/components/form/FormElementOption';
import FormFieldValidationMixin from '@/components/mixins/FormFieldValidationMixin';

export default {
  components: {
    CardPage
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    hasLloyds: {
      type: Boolean,
      default: false
    },
    lloydsSyndicate: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],

  data() {
    return {
      model: {
        VATCode: null,
        IsBankDetails: 0,
        IsReportChamberOfCommerceCertificate: 0,
        IsPIInsurance: 0,
        IsRUI: 0,
        IsVisura: 0,
        RUIReference: null,
        FinancialStatements: null,
        AdditionalName: null,
        Note: null,
        SyndicateAcronym: null,
        SyndicateNumber: null,
        AccountExecutive: null,
        PartnershipStatus: null,
        PartnershipSeniority: null,
        ClassificationDetail: null,
        Segmentation1: null,
        Segmentation2: null,
        Segmentation3: null,
        Segmentation4: null,
        NoUnderwriterExecutive: 0,
        NoAreaManager: 0,
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true
      },
      schema: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'VAT Code',
                model: 'VATCode',
                disabled: () => this.readonly,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Additional Name',
                model: 'AdditionalName',
                disabled: () => this.readonly,
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'Bank Details',
                model: 'IsBankDetails',
                disabled: () => this.readonly
              },
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'Report Chamber of Commerce Certificate',
                model: 'IsReportChamberOfCommerceCertificate',
                disabled: () => this.readonly
              },
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'PI Insurance',
                model: 'IsPIInsurance',
                disabled: () => this.readonly
              },
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'Visura',
                model: 'IsVisura',
                disabled: () => this.readonly
              },
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'RUI',
                model: 'IsRUI',
                disabled: () => this.readonly
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'RUI Reference',
                model: 'RUIReference',
                disabled: () => this.readonly
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Financial Statements',
                model: 'FinancialStatements',
                disabled: () => this.readonly
              },
              {
                type: 'textArea',
                styleClasses: 'w-full',
                label: 'Note',
                model: 'Note',
                disabled: () => this.readonly,
                rows: 2
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                visible: () => this.hasLloyds && this.lloydsSyndicate,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Syndicate Acronym',
                model: 'SyndicateAcronym',
                disabled: () => this.readonly
              },
              {
                visible: () => this.hasLloyds && this.lloydsSyndicate,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Syndicate Number',
                model: 'SyndicateNumber',
                disabled: () => this.readonly
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                type: "vueMultiSelect",
                selectOptions,
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Partnership Status',
                model: 'PartnershipStatus',
                values: () => this.partnershipStatuses,
                disabled: () => this.readonly,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                type: "vueMultiSelect",
                selectOptions,
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Partnership Seniority',
                model: 'PartnershipSeniority',
                values: () => this.partnershipSeniorityOptions,
                disabled: () => this.readonly,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                type: "vueMultiSelect",
                selectOptions,
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Classification Detail',
                model: 'ClassificationDetail',
                values: () => this.classificationDetailOptions,
                disabled: () => this.readonly,
                required: () => !this.readonly,
                validator: 'required'
              }
            ]
          }, {
            styleClasses: 'w-full',
            fields: [
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Segmentation 1',
                model: 'Segmentation1',
                disabled: () => this.readonly
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Segmentation 2',
                model: 'Segmentation2',
                disabled: () => this.readonly
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Segmentation 3',
                model: 'Segmentation3',
                disabled: () => this.readonly
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Segmentation 4',
                model: 'Segmentation4',
                disabled: () => this.readonly
              }
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'No Underwriting Executive',
                model: 'NoUnderwriterExecutive',
                disabled: () => this.readonly,
                onChanged: () => {
                  this.validateField('Contact_UnderwriterExecutive_FK');
                },
              },
              {
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3',
                label: 'Underwriting Executive',
                model: 'Contact_UnderwriterExecutive_FK',
                values: () => this.underwriterExecutivesOptions,
                selectOptions,
                disabled: () => this.readonly || this.model.NoUnderwriterExecutive == 1,
                required: () => !this.readonly && this.model.NoUnderwriterExecutive == 0,
                validator: [
                    (value, field, model) => {
                      if (model.NoUnderwriterExecutive == 1) {
                        model.Contact_UnderwriterExecutive_FK = null;
                      }

                      if (!value && model.NoUnderwriterExecutive == 0) {
                        return 'Please enter a value';
                      }

                      return true;
                    },
                ]
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'No Area Manager',
                model: 'NoAreaManager',
                disabled: () => this.readonly,
                onChanged: () => {
                  this.validateField('Contact_AreaManager_FK');
                },
              },
              {
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3',
                label: 'Area Manager',
                model: 'Contact_AreaManager_FK',
                values: () => this.areaManagersOptions,
                selectOptions,
                disabled: () => this.readonly || this.model.NoAreaManager == 1,
                required: () => !this.readonly && this.model.NoAreaManager == 0,
                validator: [
                    (value, field, model) => {
                      if (model.NoAreaManager == 1) {
                        model.Contact_AreaManager_FK = null;
                      }

                      if (!value && model.NoAreaManager == 0) {
                        return 'Please enter a value';
                      }

                      return true;
                    },
                ]
              },
            ]
          },
          {
            styleClasses: 'w-full',
            fields: [
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'No Placing Broker',
                model: 'NoPlacingBroker',
                disabled: () => this.readonly,
                onChanged: () => {
                  this.validateField('Contact_PlacingBroker_FK');
                },
              },
              {
                type: 'vueMultiSelect',
                styleClasses: 'w-1/3',
                label: 'Placing Broker',
                model: 'Contact_PlacingBroker_FK',
                values: () => this.placingBrokersOptions,
                selectOptions,
                disabled: () => this.readonly || this.model.NoPlacingBroker == 1,
                required: () => !this.readonly && this.model.NoPlacingBroker == 0,
                validator: [
                    (value, field, model) => {
                      if (model.NoPlacingBroker == 1) {
                        model.Contact_PlacingBroker_FK = null;
                      }

                      if (!value && model.NoPlacingBroker == 0) {
                        return 'Please enter a value';
                      }

                      return true;
                    },
                ]
              },
            ]
          },
        ]
      },
      parentCompanyOptions: [],
      partnershipStatuses: [],
      partnershipSeniorityOptions: [],
      classificationDetailOptions: [],
      placingBrokersOptions: [],
      areaManagersOptions: [],
      underwriterExecutivesOptions: [],
      options: [
        {
          endpoint: () => this.$api.companies.partnershipStatuses(),
          optionsFieldName: 'partnershipStatuses',
          nameFieldName: 'Type',
          idFieldName: 'PartnershipStatus',
        },
        {
          endpoint: () => this.$api.companies.partnershipSeniorityOptions(),
          optionsFieldName: 'partnershipSeniorityOptions',
          nameFieldName: 'Type',
          idFieldName: 'PartnershipSeniority',
        },
        {
          endpoint: () => this.$api.companies.classificationDetailOptions(),
          optionsFieldName: 'classificationDetailOptions',
          nameFieldName: 'Type',
          idFieldName: 'ClassificationDetail',
        },
        {
          endpoint: () => this.$api.companies.underwriterExecutives(),
          optionsFieldName: 'underwriterExecutivesOptions',
          nameFieldName: 'ContactDisplayName',
          idFieldName: 'Contact_UnderwriterExecutive_FK',
          externalIdFieldName: 'Contact_PK',
          idIsString: true
        },
        {
          endpoint: () => this.$api.companies.areaManagers(),
          optionsFieldName: 'areaManagersOptions',
          nameFieldName: 'ContactDisplayName',
          idFieldName: 'Contact_AreaManager_FK',
          externalIdFieldName: 'Contact_PK',
          idIsString: true
        },
        {
          endpoint: () => this.$api.companies.placingBrokersContacts(),
          optionsFieldName: 'placingBrokersOptions',
          nameFieldName: 'ContactDisplayName',
          idFieldName: 'Contact_PlacingBroker_FK',
          externalIdFieldName: 'Contact_PK',
          idIsString: true
        },
      ]
    };
  }
};
</script>
